import type { Difficulty, FillQuestion } from '../math/types';

// Mulberry32 — small deterministic PRNG seeded by integer
function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

/**
 * N-4-2：進位加減法——四、五位數直式計算。
 * 四上第二章：四～五位數多次進退位加減直式。
 *
 * difficulty 1：四位數 ± 四位數（單次進退位）
 * difficulty 2：四位數 ± 四位數（多次進退位，結果不超過 99999）
 * difficulty 3：五位數 ± 四/五位數（多次進退位）
 */
export function generateAddSubMulti(seed: number, difficulty: Difficulty): FillQuestion {
  const rand = mulberry32(seed);
  const isAdd = rand() > 0.5;

  let a: number;
  let b: number;

  if (difficulty === 1) {
    if (isAdd) {
      do {
        a = randInt(rand, 1000, 4999);
        b = randInt(rand, 1000, 4999);
      } while (a + b > 9999);
    } else {
      do {
        a = randInt(rand, 2000, 9999);
        b = randInt(rand, 1000, a - 100);
      } while (a - b < 100);
    }
  } else if (difficulty === 2) {
    if (isAdd) {
      do {
        a = randInt(rand, 5000, 9999);
        b = randInt(rand, 5000, 9999);
      } while (a + b > 99999);
    } else {
      do {
        a = randInt(rand, 5000, 9999);
        b = randInt(rand, 1000, a - 1);
      } while (a - b < 500);
    }
  } else {
    if (isAdd) {
      a = randInt(rand, 10000, 49999);
      b = randInt(rand, 10000, 49999);
    } else {
      do {
        a = randInt(rand, 20000, 99999);
        b = randInt(rand, 10000, a - 1);
      } while (a - b < 1000);
    }
  }

  const correct = isAdd ? a + b : a - b;
  const op = isAdd ? '+' : '−';

  return {
    kind: 'fill',
    prompt: `${a} ${op} ${b} = ?`,
    correctAnswer: String(correct),
    acceptedAnswers: [String(correct)],
    explanation: `直式計算：${a} ${op} ${b} = ${correct}`,
    kp: 'math.add.sub.multi.4up',
  };
}
